import { relations, sql } from 'drizzle-orm'
import { index, pgTable, text, uniqueIndex } from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getDateField,
  getIdReference,
  getMonetaryField,
  getNullableIdReference,
  getTimestampWithTimezone,
} from '../common/utils'
import { cashAccounts } from './cashAccount'
import { cashCertaintyEnum, cashDirectionEnum } from './cashEnums'
import { cashFlowRules } from './cashFlowRule'
import { companies } from './company'

// One dated cash movement. Every producer (bank import, invoices, payroll,
// hand-entered rows) emits this shape; the forecast is a running sum of them.
// `transfer` moves money between a company's own accounts and nets to zero
// at company level.
export const cashEvents = pgTable(
  'cash_events',
  {
    ...getBaseFields(),
    companyId: getIdReference(() => companies.id),
    accountId: getNullableIdReference(() => cashAccounts.id),
    date: getDateField().notNull(),
    // The day the row belongs to in the books, when that differs from the day
    // the cash moves: a purchase invoice's own date decides its VAT month,
    // whether it is paid on time, late or not yet at all.
    accrualDate: getDateField(),
    amount: getMonetaryField().notNull(), // always positive; direction carries the sign
    direction: cashDirectionEnum().notNull(),
    certainty: cashCertaintyEnum().notNull(),
    category: text().notNull(),
    source: text().notNull(), // provenance, e.g. 'erp-invoice', 'payroll', 'seed-excel'
    description: text(),
    // Set on both legs of an internal invoice so the group view can drop them.
    intercompanyCounterpartyCompanyId: getNullableIdReference(
      () => companies.id,
    ),
    ruleId: getNullableIdReference(() => cashFlowRules.id),
    // Set when someone edits a generated occurrence by hand. Regeneration then
    // leaves the row alone, so the edit survives every later save of the rule.
    overriddenAt: getTimestampWithTimezone(),
    // The producer's own key for this row, unique within `source`, so an import
    // or a regeneration can recognise what it wrote last time.
    externalRef: text(),
  },
  (table) => [
    ...getBaseIndexes('cash_events', table),
    index('cash_events_company_date_idx').on(table.companyId, table.date),
    index('cash_events_rule_id_idx').on(table.ruleId),
    // Per company: both Fennoa companies number their invoices from 1.
    uniqueIndex('cash_events_source_external_ref_unique_idx')
      .on(table.companyId, table.source, table.externalRef)
      .where(sql`external_ref IS NOT NULL AND deleted_at IS NULL`),
  ],
)

export const cashEventsRelations = relations(cashEvents, ({ one }) => ({
  company: one(companies, {
    fields: [cashEvents.companyId],
    references: [companies.id],
  }),
  account: one(cashAccounts, {
    fields: [cashEvents.accountId],
    references: [cashAccounts.id],
  }),
  rule: one(cashFlowRules, {
    fields: [cashEvents.ruleId],
    references: [cashFlowRules.id],
  }),
}))
